<template>
	<div class="stock-shortage-requests-dashboard w-full">
		<!-- Header -->
		<div class="pt-0">
			<app-title-bar :icon="['fas', 'fa-clipboard-check']" subtitle="Stock Management" title="Stock Shortage Requests">
				<div class="flex items-center space-x-3">
					<app-badge v-if="pendingCount > 0" theme="warning" size="medium">
						{{ pendingCount }} Pending Requests
					</app-badge>
					<app-button :loading="loading" size="small" theme="outline" @click="refreshData">
						<font-awesome-icon icon="fas fa-sync" class="mr-2" />
						Refresh
					</app-button>
				</div>
			</app-title-bar>

			<!-- Filters -->
			<div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
				<div class="text-xs font-semibold text-gray-800 mb-3">Filter Shortage Requests:</div>
				<div class="flex flex-wrap items-start gap-x-10 gap-y-4">
					<fieldset>
						<legend class="text-xs font-medium text-gray-500 mb-2">Status</legend>
						<div class="flex flex-wrap gap-2">
							<button
								v-for="option in statusFilterOptions"
								:key="option.value"
								:class="[
									'px-3 py-1.5 text-xs rounded-md border transition-all',
									filters.statuses.includes(option.value)
										? 'bg-mabey-600 text-white border-mabey-600'
										: 'bg-white text-gray-700 border-gray-300 hover:border-mabey-400'
								]"
								@click="toggleStatusFilter(option.value)">
								<font-awesome-icon
									:icon="filters.statuses.includes(option.value) ? 'fas fa-check-square' : 'far fa-square'"
									class="mr-1" />
								{{ option.label }}
							</button>
						</div>
					</fieldset>
					<fieldset>
						<legend class="text-xs font-medium text-gray-500 mb-2">Show</legend>
						<div class="flex flex-wrap items-center gap-x-5 gap-y-2">
							<label class="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
								<input
									v-model="claimFilter"
									type="radio"
									name="claim-filter"
									value="all"
									class="h-4 w-4 border-gray-300 text-mabey-600 focus:ring-mabey-500" />
								All shortage requests
							</label>
							<label class="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
								<input
									v-model="claimFilter"
									type="radio"
									name="claim-filter"
									value="mine"
									class="h-4 w-4 border-gray-300 text-mabey-600 focus:ring-mabey-500" />
								My shortage requests
							</label>
						</div>
					</fieldset>
				</div>
			</div>

			<!-- Main Tab Navigation -->
			<div class="border-b border-gray-200">
				<nav class="-mb-px flex space-x-8 ml-1">
					<button
						v-if="isStockController"
						:class="[
							mainTab === 'allocation'
								? 'border-mabey-500 text-mabey-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
							'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200'
						]"
						@click="mainTab = 'allocation'">
						<font-awesome-icon icon="fas fa-list" class="mr-2" />
						Stock Sourcing
					</button>
					<button
						v-if="isStockController"
						:class="[
							mainTab === 'flagged'
								? 'border-mabey-500 text-mabey-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
							'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200'
						]"
						@click="mainTab = 'flagged'">
						<font-awesome-icon icon="fas fa-truck-arrow-right" class="mr-2" />
						Flagged for Transfer
					</button>
					<button
						:class="[
							mainTab === 'requests'
								? 'border-mabey-500 text-mabey-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
							'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200'
						]"
						@click="mainTab = 'requests'">
						<font-awesome-icon icon="fas fa-clipboard" class="mr-2" />
						Shortage Requests
					</button>
				</nav>
			</div>
		</div>

		<!-- Requests Tab -->
		<div v-show="mainTab === 'requests'" class="pt-3">
			<!-- Filters -->
			<div class="bg-white rounded-lg border border-gray-200 p-3 mb-3 space-y-4">
				<!-- Sort Row -->
				<div>
					<div class="flex items-center gap-2">
						<span class="text-xs font-medium text-gray-500">Sort:</span>
						<button
							v-for="option in sortOptions"
							:key="option.value"
							:class="[
								'px-2.5 py-1 text-xs rounded-md border transition-all',
								sortBy === option.value
									? 'bg-mabey-600 text-white border-mabey-600'
									: 'bg-white text-gray-700 border-gray-300 hover:border-mabey-400'
							]"
							@click="sortBy = option.value">
							{{ option.label }}
						</button>
						<button
							class="p-1 text-gray-500 hover:text-gray-900 transition-colors"
							@click="sortOrder = sortOrder === 'asc' ? 'desc' : 'asc'">
							<font-awesome-icon :icon="sortOrder === 'asc' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'" />
						</button>
					</div>
				</div>

				<!-- Search and Filters Row -->
				<div class="flex flex-wrap gap-2 w-full">
					<div class="flex-1" style="min-width: 400px;">
						<div class="relative">
							<input
								v-model="filters.search"
								type="text"
								placeholder="Search by contract, item code or depot..."
								class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400" />
							<font-awesome-icon icon="fas fa-search" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
						</div>
					</div>
					<div style="min-width: 160px;">
						<select
							v-model="filters.depot"
							class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400">
							<option value="">All Depots</option>
							<option v-for="depot in uniqueDepots" :key="depot" :value="depot">{{ depot }}</option>
						</select>
					</div>
					<div style="min-width: 160px;">
						<select
							v-model="filters.contract"
							class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400">
							<option value="">All Contracts</option>
							<option v-for="contract in uniqueContracts" :key="contract" :value="contract">{{ contract }}</option>
						</select>
					</div>
				</div>

				<!-- Pending TOS Toggle -->
				<div class="flex flex-wrap items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
					<label class="flex cursor-pointer items-center gap-3 text-sm text-gray-800">
						<input
							v-model="showPendingTosOnly"
							type="checkbox"
							class="h-4 w-4 rounded border-gray-300 text-mabey-600 focus:ring-mabey-500" />
						<span class="font-medium">Show Outstanding Transfers</span>
					</label>
					<div class="text-xs text-blue-700">
						<template v-if="showPendingTosOnly">
							{{ pendingTosLineCount }} pending TOS line{{ pendingTosLineCount !== 1 ? 's' : '' }}
						</template>
						<template v-else>
							Loads all request statuses while enabled
						</template>
					</div>
				</div>

				<!-- Stats and Clear Filters Row -->
				<div class="flex items-center justify-between">
					<div class="text-xs text-gray-500">
						{{ filteredRequests.length }} of {{ requests.length }} requests
					</div>
					<button
						v-if="filters.search || filters.depot || filters.contract || showPendingTosOnly"
						class="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors flex items-center space-x-1 whitespace-nowrap"
						@click="clearRequestFilters">
						<font-awesome-icon icon="fas fa-times" />
						<span>Clear Filters</span>
					</button>
				</div>
			</div>

			<!-- Loading State -->
			<div v-if="loading && requests.length === 0" class="flex justify-center items-center py-12">
				<app-loader />
			</div>

			<!-- Empty State -->
			<div
				v-else-if="!loading && filteredRequests.length === 0"
				class="bg-white rounded-lg border border-gray-200 p-12 text-center">
				<font-awesome-icon icon="fas fa-inbox" class="text-gray-300 text-5xl mb-4" />
				<h3 class="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
				<p class="text-gray-600">
					{{ requests.length === 0 ? 'No stock shortage requests yet.' : 'Try adjusting your filters.' }}
				</p>
			</div>

			<!-- Requests List -->
			<div v-else class="space-y-2">
				<div
					v-for="request in filteredRequests"
					:key="request.id"
					:ref="(el) => setRequestRef(request.id, el)"
					class="bg-white rounded-lg border overflow-hidden transition-all"
					:class="focusedRequestId === request.id ? 'border-mabey-500 shadow-lg focused-request' : 'border-gray-200'">

					<!-- Request Header -->
					<div class="px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors" @click="toggleRequestExpanded(request.id)">
						<!-- Top row: key info -->
						<div class="flex items-center gap-3 flex-wrap">
							<svg class="w-4 h-4 text-gray-400 transition-transform flex-shrink-0" :class="{'rotate-90': expandedRequests.has(request.id)}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
							</svg>
							<span class="text-sm font-bold text-gray-900">{{ request.contract_number }}</span>
							<app-badge :theme="getStatusTheme(request.status)" size="small">
								{{ request.status.toUpperCase() }}
							</app-badge>
							<div class="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
								<span>{{ request.depot }}</span>
							</div>
							<span class="text-xs text-gray-500">{{ request.items.length }} item{{ request.items.length !== 1 ? 's' : '' }}</span>
							<span v-if="hasUrgentItems(request)" class="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-200">
								<font-awesome-icon icon="fas fa-exclamation-triangle" class="mr-1" />URGENT
							</span>
							<div class="ml-auto flex flex-wrap items-center justify-end gap-2">
								<div class="flex h-11 min-w-[170px] flex-col justify-center border-y border-gray-200 px-3">
									<div class="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Raised by</div>
									<div class="flex items-center gap-1.5 text-xs">
										<span class="font-semibold text-gray-900">{{ request.requested_by }}</span>
										<span class="text-gray-300">·</span>
										<span class="text-gray-500">{{ formatShortDate(request.requested_at) }}</span>
									</div>
								</div>
								<span class="text-lg font-light text-gray-300" aria-hidden="true">|</span>
								<div
									class="flex h-11 min-w-[140px] flex-col justify-center border-y px-3"
									:class="request.claimed_by ? 'border-mabey-200' : 'border-gray-200'">
									<div class="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Claimed by</div>
									<div
										class="text-xs font-semibold"
										:class="request.claimed_by ? 'text-mabey-800' : 'text-gray-500'">
										{{ request.claimed_by?.name || 'Unclaimed' }}
									</div>
								</div>
								<div @click.stop>
									<button
										v-if="isStockController"
										type="button"
										class="inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs font-semibold transition-colors mr-2"
										:class="canChangeDestinationDepot(request)
											? 'border-gray-300 bg-white text-gray-700 hover:border-mabey-400 hover:text-mabey-700'
											: 'border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100'"
										:title="getDepotChangeButtonTitle(request)"
										@click.stop="openDepotChangeModal(request)">
										<font-awesome-icon icon="fas fa-pen" />
										<span>Edit depot</span>
									</button>
									<select
											v-if="isStockController"
											:value="request.status"
											class="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-mabey-400 bg-white cursor-pointer"
											@change="request.status = (($event.target as HTMLSelectElement).value as RequestStatus); updateRequestStatus(request)">
										<option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
									</select>
									<span v-else class="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-200">
										{{ statusOptions.find(o => o.value === request.status)?.label || request.status }}
									</span>
								</div>
							</div>
						</div>

						<!-- Request Details - always visible, highlighted -->
						<div class="mt-2 ml-7 rounded px-3 py-2 text-sm" :class="request.request_details ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'">
							<span class="font-semibold text-xs uppercase tracking-wide mr-2" :class="request.request_details ? 'text-blue-700' : 'text-gray-400'">Details:</span>
							<span v-if="request.request_details" class="text-gray-800">{{ request.request_details }}</span>
							<span v-else class="text-gray-400 italic">No details provided</span>
						</div>
						<!-- Stock Controller Notes -->
						<div
							v-if="isStockController || request.stock_controller_notes"
							class="mt-2 ml-7 rounded border px-3 py-2 text-sm"
							:class="request.stock_controller_notes ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'"
							@click.stop>
							<div class="mb-2 flex items-center justify-between gap-3">
								<span
									class="font-semibold text-xs uppercase tracking-wide"
									:class="request.stock_controller_notes ? 'text-amber-700' : 'text-gray-500'">
									Stock Controller Notes
								</span>
								<button
									v-if="isStockController"
									:disabled="!isControllerNotesDirty(request) || savingControllerNotesId === request.id"
									class="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold transition-colors"
									:class="isControllerNotesDirty(request) && savingControllerNotesId !== request.id ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'"
									@click.stop="saveControllerNotes(request)">
									<font-awesome-icon
										v-if="savingControllerNotesId === request.id"
										icon="fas fa-spinner"
										spin />
									<font-awesome-icon v-else icon="fas fa-save" />
									Save Notes
								</button>
							</div>
							<textarea
								v-if="isStockController"
								:value="getControllerNotesDraft(request)"
								rows="3"
								class="w-full resize-y rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-mabey-400 focus:outline-none focus:ring-2 focus:ring-mabey-400"
								placeholder="Add stock controller notes for status tracking..."
								@click.stop
								@input="controllerNotesDrafts[request.id] = ($event.target as HTMLTextAreaElement).value"></textarea>
							<p v-else class="whitespace-pre-wrap text-gray-800">{{ request.stock_controller_notes }}</p>
						</div>
					</div>

					<!-- Items Detail Table -->
					<div v-if="expandedRequests.has(request.id)" class="border-t border-gray-200 overflow-hidden max-w-full">
						<!-- Top Scrollbar -->
						<div
							:ref="(el) => { if (el) topScrollContainers[request.id] = el as HTMLElement }"
							class="overflow-x-auto overflow-y-hidden w-full border-b border-gray-100"
							@scroll="syncScroll(topScrollContainers[request.id], tableContainers[request.id])">
							<div class="h-px" style="min-width: 800px"></div>
						</div>

						<div
							:ref="(el) => { if (el) tableContainers[request.id] = el as HTMLElement }"
							class="overflow-x-auto overflow-y-hidden w-full"
							@scroll="syncScroll(tableContainers[request.id], topScrollContainers[request.id])">
							<table class="w-full text-xs min-w-[800px]">
								<thead>
									<tr class="bg-gray-50 border-b border-gray-200">
										<th class="px-3 py-2 text-left font-semibold text-gray-600">Item Code</th>
										<th class="px-3 py-2 text-left font-semibold text-gray-600">Description</th>
										<th class="px-3 py-2 text-center font-semibold text-gray-600 hidden md:table-cell">Type</th>
										<th class="px-3 py-2 text-right font-semibold text-gray-600">Qty Requested</th>
										<th class="px-3 py-2 text-right font-semibold text-gray-600">Qty Allocated</th>
										<th class="px-3 py-2 text-left font-semibold text-gray-600 hidden sm:table-cell">Required By</th>
										<th class="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
										<th class="px-3 py-2 text-left font-semibold text-gray-600 hidden lg:table-cell">From Depot</th>
										<th class="px-3 py-2 text-left font-semibold text-gray-600 hidden xl:table-cell">Target Delivery</th>
										<th class="px-3 py-2 text-left font-semibold text-gray-600 hidden lg:table-cell">TOS No</th>
									</tr>
								</thead>
								<tbody class="divide-y divide-gray-100">
									<tr v-for="(item, idx) in getVisibleItems(request)" :key="idx" class="hover:bg-gray-50 transition-colors">
										<td class="px-3 py-1.5">
											<div class="flex items-center space-x-1.5">
												<button
													class="font-medium text-mabey-600 hover:text-mabey-700 hover:underline transition-colors text-left"
													@click="navigateToAllocation(request.id, item.item_code)">
													{{ item.item_code }}
												</button>
												<span v-if="shouldShowSplitBadge(item, request)" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700">
													SPLIT
												</span>
											</div>
										</td>
										<td class="px-3 py-1.5 text-gray-700">
											<div class="max-w-[300px] whitespace-normal leading-tight" :title="item.description">{{ item.description }}</div>
										</td>
										<td class="px-3 py-1.5 text-center hidden md:table-cell">
											<span
												:class="item.type === 'shortage' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'"
												class="font-semibold px-1.5 py-0.5 rounded text-[10px]">
												{{ item.type === 'shortage' ? 'SHORT' : 'EXTRA' }}
											</span>
										</td>
										<td class="px-3 py-1.5 text-right font-semibold text-gray-900">{{ item.quantity_requested }}</td>
										<td class="px-3 py-1.5 text-right font-medium text-gray-900">
											{{ item.allocation_quantity !== null && item.allocation_quantity !== undefined ? item.allocation_quantity : '—' }}
										</td>
										<td class="px-3 py-1.5 text-gray-700 hidden sm:table-cell">{{ formatShortDate(item.required_by_date) }}</td>
										<td class="px-3 py-1.5">
											<app-badge v-if="item.fulfillment_status" :theme="getFulfillmentStatusTheme(item.fulfillment_status)" size="small">
												{{ item.fulfillment_status.replace('-', ' ').toUpperCase() }}
											</app-badge>
											<span v-else class="text-gray-400">—</span>
										</td>
										<td class="px-3 py-1.5 text-gray-700 hidden lg:table-cell">{{ item.allocation_from_depot || '—' }}</td>
										<td class="px-3 py-1.5 text-gray-700 hidden xl:table-cell">{{ item.target_delivery_date ? formatShortDate(item.target_delivery_date) : '—' }}</td>
										<td class="px-3 py-1.5 hidden lg:table-cell">
											<span v-if="item.tos_no" class="font-mono text-blue-700 font-medium">{{ item.tos_no }}</span>
											<span v-else class="text-gray-400">—</span>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Allocation Tab -->
		<div v-show="mainTab === 'allocation'" class="pt-3">
			<div v-if="loading && requests.length === 0" class="flex justify-center items-center py-12">
				<app-loader />
			</div>
			<stock-sourcing-view v-else :requests="requests" :item-status-filter="itemStatusFilter" :focus-filter="allocationFocusFilter" @updated="(req) => handleRequestUpdated(req)" @open-request="navigateToRequest" />
		</div>

		<!-- TOS Builder Tab -->
		<div v-show="mainTab === 'flagged'" class="pt-3">
			<div v-if="loading && requests.length === 0" class="flex justify-center items-center py-12">
				<app-loader />
			</div>
			<tos-builder-view v-else :requests="requests" @updated="(req) => handleRequestUpdated(req)" @focus-request="focusOnRequest" @navigate-to-allocation="navigateToAllocation" />
		</div>

	</div>

	<stock-destination-depot-modal
		v-if="depotChangeRequest"
		:is-open="Boolean(depotChangeRequest)"
		:request="depotChangeRequest"
		:depot-options="depotOptions"
		:saving="changingDepot"
		@close="closeDepotChangeModal"
		@confirm="changeDestinationDepot" />
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted, watch, nextTick } from 'vue'
	import type { StockShortageResponse, StockShortageItem, StockShortageRequest } from '@/data/schema/stock-shortage'
	import DepotService from '@/services/depot'
	import StockService from '@/services/stock'
	import useToaster from '@/stores/toaster'
	import useUserStore from '@/stores/auth'
	import AppButton from '@/components/base/AppButton.vue'
	import AppBadge from '@/components/base/AppBadge.vue'
	import AppLoader from '@/components/base/AppLoader.vue'
	import StockSourcingView from './StockSourcingView.vue'
	import TosBuilderView from './TosBuilderView.vue'
	import StockDestinationDepotModal from './StockDestinationDepotModal.vue'

	type RequestStatus = StockShortageResponse['status']
	type SortBy = 'date' | 'depot' | 'status'
	type ClaimFilter = 'all' | 'mine'

	const stockService = new StockService()
	const depotService = new DepotService()
	const toaster = useToaster()
	const userStore = useUserStore()

	const loading = ref(false)
	const requests = ref<StockShortageResponse[]>([])
	const claimFilter = ref<ClaimFilter>('all')
	const focusedRequestId = ref<string | null>(null)
	const controllerNotesDrafts = ref<Record<string, string>>({})
	const savingControllerNotesId = ref<string | null>(null)
	const depotOptions = ref<string[]>([])
	const depotChangeRequest = ref<StockShortageResponse | null>(null)
	const changingDepot = ref(false)

	const isStockController = computed(() => {
		return userStore.isAbleTo(['view-stock-controller'])
	})

	const mainTab = ref<'requests' | 'allocation' | 'flagged'>(
		!isStockController.value
			? 'requests'
			: (localStorage.getItem('stockControllerTab') as 'requests' | 'allocation' | 'flagged') || 'allocation'
	)
	const requestRefs = ref<Record<string, HTMLElement>>({})
	const expandedRequests = ref<Set<string>>(new Set())
	const showPendingTosOnly = ref(false)

	const topScrollContainers = ref<Record<string, HTMLElement>>({})
	const tableContainers = ref<Record<string, HTMLElement>>({})

	const syncScroll = (source: HTMLElement | undefined, target: HTMLElement | undefined) => {
		if (source && target) {
			target.scrollLeft = source.scrollLeft
		}
	}

	const filters = ref({
		search: '',
		statuses: ['pending'] as string[],
		depot: '',
		contract: ''
	})

	const itemStatusFilter = ref<string[]>(['unfulfilled'])

	const sortBy = ref<SortBy>('date')
	const sortOrder = ref<'asc' | 'desc'>('desc')

	const statusFilterOptions = [
		{ label: 'Pending', value: 'pending' },
		{ label: 'Rejected', value: 'rejected' },
		{ label: 'Completed', value: 'completed' }
	]

	const statusOptions: Array<{ label: string; value: RequestStatus }> = [
		{ label: 'Pending', value: 'pending' },
		{ label: 'Rejected', value: 'rejected' },
		{ label: 'Completed', value: 'completed' }
	]

	const sortOptions: Array<{ label: string; value: SortBy }> = [
		{ label: 'Date', value: 'date' },
		{ label: 'Depot', value: 'depot' },
		{ label: 'Status', value: 'status' }
	]

	const pendingCount = computed(() => {
		return requests.value.filter(r => r.status === 'pending').length
	})

	const isPendingTosItem = (item: StockShortageItem): boolean => {
		return item.fulfillment_status === 'pending-fulfillment' && Boolean(item.tos_no)
	}

	const pendingTosLineCount = computed(() => {
		return requests.value.reduce((count, request) => count + request.items.filter(isPendingTosItem).length, 0)
	})

	const uniqueDepots = computed(() => {
		return [...new Set([...requests.value.map(r => r.depot), ...depotOptions.value].filter(Boolean))].sort()
	})

	const uniqueContracts = computed(() => {
		return [...new Set(requests.value.map(r => r.contract_number))].sort()
	})

	const shouldShowSplitBadge = (item: StockShortageItem, request: StockShortageRequest): boolean => {
		if (!item.split_group_id) return false

		// Count items with this split_group_id
		const itemsInGroup = request.items.filter(i => i.split_group_id === item.split_group_id)
		return itemsInGroup.length > 1
	}

	const getSortedItems = (items: StockShortageItem[]): StockShortageItem[] => {
		// Group items by split_group_id
		const splitGroups = new Map<string, StockShortageItem[]>()
		const nonSplitItems: StockShortageItem[] = []

		items.forEach(item => {
			if (item.split_group_id) {
				if (!splitGroups.has(item.split_group_id)) {
					splitGroups.set(item.split_group_id, [])
				}
				splitGroups.get(item.split_group_id)!.push(item)
			} else {
				nonSplitItems.push(item)
			}
		})

		// Sort each split group: original (no split_from_id) first, then split items
		splitGroups.forEach(group => {
			group.sort((a, b) => {
				if (!a.split_from_id && b.split_from_id) return -1
				if (a.split_from_id && !b.split_from_id) return 1
				return 0
			})
		})

		// Combine: non-split items first, then split groups
		const result: StockShortageItem[] = [...nonSplitItems]
		splitGroups.forEach(group => {
			result.push(...group)
		})

		return result
	}

	const getVisibleItems = (request: StockShortageResponse): StockShortageItem[] => {
		const items = showPendingTosOnly.value
			? request.items.filter(isPendingTosItem)
			: request.items

		return getSortedItems(items)
	}

	const filteredRequests = computed(() => {
		let filtered = [...requests.value]

		// Apply filters
		if (!showPendingTosOnly.value && filters.value.statuses.length > 0) {
			filtered = filtered.filter(r => filters.value.statuses.includes(r.status))
		}

		if (showPendingTosOnly.value) {
			filtered = filtered.filter(r => r.items.some(isPendingTosItem))
		}

		if (filters.value.depot) {
			filtered = filtered.filter(r => r.depot === filters.value.depot)
		}

		if (filters.value.contract) {
			filtered = filtered.filter(r => r.contract_number === filters.value.contract)
		}

		if (filters.value.search) {
			const search = filters.value.search.toLowerCase()
			filtered = filtered.filter(
				r =>
					r.contract_number.toLowerCase().includes(search) ||
					r.depot.toLowerCase().includes(search) ||
					r.requested_by.toLowerCase().includes(search) ||
					r.request_details?.toLowerCase().includes(search) ||
					r.stock_controller_notes?.toLowerCase().includes(search) ||
					r.items.some(item => item.item_code.toLowerCase().includes(search) || item.description?.toLowerCase().includes(search))
			)
		}

		// Apply sorting
		filtered.sort((a, b) => {
			let comparison = 0

			switch (sortBy.value) {
				case 'date':
					comparison = new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime()
					break
				case 'depot':
					comparison = a.depot.localeCompare(b.depot)
					break
				case 'status':
					comparison = a.status.localeCompare(b.status)
					break
			}

			return sortOrder.value === 'asc' ? comparison : -comparison
		})

		return filtered
	})

	const getStatusTheme = (status: string) => {
		const themes: Record<string, any> = {
			pending: 'warning',
			rejected: 'danger',
			completed: 'info'
		}
		return themes[status] || 'neutral'
	}


	const getFulfillmentStatusTheme = (status: string) => {
		const themes: Record<string, any> = {
			'unfulfilled': 'danger',
			'part-fulfilled': 'warning',
			'flagged-for-transfer': 'info',
			'rejected': 'danger',
			'pending-fulfillment': 'success',
			'fulfilled': 'success'
		}
		return themes[status] || 'neutral'
	}

	const formatShortDate = (dateString: string) => {
		if (!dateString) return '—'
		const date = new Date(dateString)
		return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
	}

	const hasUrgentItems = (request: StockShortageResponse) => {
		const twoDaysFromNow = new Date()
		twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2)

		return request.items.some(item => {
			const requiredDate = new Date(item.required_by_date)
			return requiredDate <= twoDaysFromNow && item.fulfillment_status !== 'fulfilled'
		})
	}

	const toggleStatusFilter = (status: string) => {
		const index = filters.value.statuses.indexOf(status)
		if (index > -1) {
			filters.value.statuses.splice(index, 1)
		} else {
			filters.value.statuses.push(status)
		}
	}

	const clearRequestFilters = () => {
		filters.value.search = ''
		filters.value.depot = ''
		filters.value.contract = ''
		showPendingTosOnly.value = false
	}

	const loadRequests = async () => {
		loading.value = true
		try {
			const response: any = await stockService.getShortageRequests({
				status: !showPendingTosOnly.value && filters.value.statuses.length > 0 ? filters.value.statuses : undefined,
				myrequests: claimFilter.value === 'mine'
			}, userStore.user?.assigned_depots)
			let loadedRequests: StockShortageResponse[] = []
			// Handle different response formats - API might return { data: [...] } or just [...]
			if (Array.isArray(response)) {
				loadedRequests = response
			} else if (response && Array.isArray(response.data)) {
				loadedRequests = response.data
			} else if (response && response.data && Array.isArray(response.data.data)) {
				loadedRequests = response.data.data
			} else {
				console.warn('Unexpected API response format:', response)
				loadedRequests = []
			}
			requests.value = loadedRequests
			syncControllerNotesDrafts(loadedRequests)
		} catch (error) {
			console.error('Error loading shortage requests:', error)
			toaster.error({ text: 'Failed to load shortage requests' })
			requests.value = []
		} finally {
			loading.value = false
		}
	}

	const syncControllerNotesDrafts = (loadedRequests: StockShortageResponse[]) => {
		const drafts = { ...controllerNotesDrafts.value }
		loadedRequests.forEach((request) => {
			drafts[request.id] = request.stock_controller_notes || ''
		})
		controllerNotesDrafts.value = drafts
	}

	const getControllerNotesDraft = (request: StockShortageResponse) => {
		if (controllerNotesDrafts.value[request.id] === undefined) {
			controllerNotesDrafts.value[request.id] = request.stock_controller_notes || ''
		}
		return controllerNotesDrafts.value[request.id]
	}

	const isControllerNotesDirty = (request: StockShortageResponse) => {
		return getControllerNotesDraft(request) !== (request.stock_controller_notes || '')
	}

	const saveControllerNotes = async (request: StockShortageResponse) => {
		if (!isStockController.value || !isControllerNotesDirty(request)) return

		savingControllerNotesId.value = request.id
		try {
			const notes = getControllerNotesDraft(request)
			await stockService.updateShortageRequest(request.id, {
				stock_controller_notes: notes
			})
			request.stock_controller_notes = notes
			toaster.success({ text: 'Stock controller notes saved' })
		} catch (error) {
			console.error('Error saving stock controller notes:', error)
			toaster.error({ text: 'Failed to save stock controller notes' })
		} finally {
			savingControllerNotesId.value = null
		}
	}

	const extractDepotCode = (depot: any) => {
		return depot?.code || depot?.prefix || depot?.name || depot
	}

	const loadDepotOptions = async () => {
		try {
			const response = await depotService.getAll({}, undefined)
			const payload = (response as any)?.data?.data || (response as any)?.data || response
			const depots = Array.isArray(payload) ? payload : []
			depotOptions.value = depots
				.map(extractDepotCode)
				.filter(Boolean)
				.sort((a: string, b: string) => a.localeCompare(b))
		} catch (error) {
			console.error('Error loading depot options:', error)
			depotOptions.value = []
		}
	}

	const openDepotChangeModal = (request: StockShortageResponse) => {
		depotChangeRequest.value = request
	}

	const closeDepotChangeModal = () => {
		if (changingDepot.value) return
		depotChangeRequest.value = null
	}

	const getDepotChangeBlockedItems = (request: StockShortageResponse) => {
		return request.items.filter((item) => {
			const status = item.fulfillment_status || 'unfulfilled'
			return Boolean(
				item.tos_no ||
					status === 'pending-fulfillment' ||
					status === 'fulfilled' ||
					status === 'part-fulfilled'
			)
		})
	}

	const canChangeDestinationDepot = (request: StockShortageResponse) => {
		return getDepotChangeBlockedItems(request).length === 0
	}

	const getDepotChangeButtonTitle = (request: StockShortageResponse) => {
		if (canChangeDestinationDepot(request)) {
			return 'Change destination depot'
		}
		return 'Destination depot cannot be changed once any item has a TOS, is pending fulfillment, fulfilled, or part fulfilled'
	}

	const buildDepotChangeNotes = (request: StockShortageResponse, newDepot: string, reason: string) => {
		const note = [
			`[${new Date().toLocaleString('en-GB')}] Destination depot changed from ${request.depot} to ${newDepot}.`,
			`Reason: ${reason}`
		].join(' ')

		return [request.stock_controller_notes?.trim(), note].filter(Boolean).join('\n')
	}

	const changeDestinationDepot = async (payload: { depot: string; reason: string }) => {
		const request = depotChangeRequest.value
		if (!request || !isStockController.value) return

		const blockedItems = getDepotChangeBlockedItems(request)
		if (blockedItems.length > 0) {
			toaster.error({ text: 'Destination depot cannot be changed after TOS or fulfillment has started' })
			return
		}

		changingDepot.value = true

		try {
			await stockService.updateShortageRequest(request.id, {
				depot: payload.depot,
				stock_controller_notes: buildDepotChangeNotes(request, payload.depot, payload.reason)
			})

			const requestResponse = await stockService.getShortageRequest(request.id)
			const updatedRequest =
				(requestResponse as any)?.data?.data ||
				(requestResponse as any)?.data ||
				requestResponse

			await handleRequestUpdated(updatedRequest)
			depotChangeRequest.value = null
			focusedRequestId.value = request.id
			expandedRequests.value = new Set([request.id])
			toaster.success({ text: `Destination depot changed to ${payload.depot}` })
		} catch (error) {
			console.error('Error changing destination depot:', error)
			toaster.error({ text: 'Failed to change destination depot' })
		} finally {
			changingDepot.value = false
		}
	}

	const refreshData = () => {
		loadRequests()
	}

	const toggleRequestExpanded = (id: string) => {
		const newSet = new Set(expandedRequests.value)
		if (newSet.has(id)) {
			newSet.delete(id)
		} else {
			newSet.add(id)
		}
		expandedRequests.value = newSet
	}

	const setRequestRef = (id: string, el: any) => {
		if (el) {
			requestRefs.value[id] = el as HTMLElement
		}
	}

	const navigateToRequest = (request: StockShortageResponse) => {
		mainTab.value = 'requests'
		focusedRequestId.value = request.id

		// Close all expandables first
		expandedRequests.value = new Set()

		// Wait for DOM update, then open the clicked request and scroll to it
		nextTick(() => {
			// Open only the clicked request
			const newSet = new Set<string>()
			newSet.add(request.id)
			expandedRequests.value = newSet

			// Wait a bit more for the expand animation, then scroll
			setTimeout(() => {
				const element = requestRefs.value[request.id]
				if (element) {
					element.scrollIntoView({ behavior: 'smooth', block: 'center' })
				}
			}, 150)
		})

		// Clear focus highlight after 3 seconds
		setTimeout(() => {
			focusedRequestId.value = null
		}, 3000)
	}

	const focusOnRequest = (requestId: string) => {
		// Switch to requests tab
		mainTab.value = 'requests'

		// Highlight the request
		focusedRequestId.value = requestId

		// Close all expandables first
		expandedRequests.value = new Set()

		// Wait for DOM update, then open the request
		nextTick(() => {
			// Open only the clicked request
			const newSet = new Set<string>()
			newSet.add(requestId)
			expandedRequests.value = newSet

			// Scroll to the request with better positioning
			setTimeout(() => {
				const element = requestRefs.value[requestId]
				if (element) {
					const elementTop = element.getBoundingClientRect().top + window.scrollY
					const offset = 100 // Offset from top of viewport
					window.scrollTo({
						top: elementTop - offset,
						behavior: 'smooth'
					})
				}
			}, 150)
		})

		// Clear focus highlight after 3 seconds
		setTimeout(() => {
			focusedRequestId.value = null
		}, 3000)
	}

	const allocationFocusFilter = ref<{ requestId: string; itemCode: string } | null>(null)

	const navigateToAllocation = (requestId: string, itemCode: string) => {
		// Set focus filter before switching tab
		allocationFocusFilter.value = { requestId, itemCode }

		// Switch to allocation tab
		mainTab.value = 'allocation'

		nextTick(() => {
			window.scrollTo({ top: 0, behavior: 'smooth' })
		})
	}

	const updateRequestStatus = async (request: StockShortageResponse) => {
		try {
			await stockService.updateShortageRequest(request.id, {
				status: request.status
			})
			toaster.success({ text: `Request status updated to ${request.status}` })
			await loadRequests()
		} catch (error) {
			console.error('Error updating request status:', error)
			toaster.error({ text: 'Failed to update request status' })
			await loadRequests()
		}
	}

	const handleRequestUpdated = async (updatedRequest?: StockShortageResponse) => {
		if (updatedRequest) {
			if (
				claimFilter.value === 'mine' &&
				updatedRequest.claimed_by_user_id !== userStore.user?.id
			) {
				requests.value = requests.value.filter(request => request.id !== updatedRequest.id)
				return
			}

			const index = requests.value.findIndex(r => r.id === updatedRequest.id)
			if (index !== -1) {
				const newRequests = [...requests.value]
				newRequests[index] = updatedRequest
				requests.value = newRequests
				syncControllerNotesDrafts(newRequests)
				await nextTick()
			}
		} else {
			await loadRequests()
		}
	}

	onMounted(() => {
		// Clear stale caches on fresh page load
		localStorage.removeItem('tosBuilderSelections')
		localStorage.removeItem('depotDistances')
		loadDepotOptions()
		loadRequests()
	})

	// Reload data when status filters change
	watch(() => filters.value.statuses, () => {
		loadRequests()
	}, { deep: true })

	watch(showPendingTosOnly, () => {
		loadRequests()
	})

	watch(claimFilter, () => {
		loadRequests()
	})

	// Clean up request focus state when switching tabs
	watch(mainTab, (newTab) => {
		focusedRequestId.value = null
		expandedRequests.value = new Set()
		// Persist tab selection to localStorage
		localStorage.setItem('stockControllerTab', newTab)
	})

	// Auto-expand focused request
	watch(focusedRequestId, (newId) => {
		if (newId) {
			const newSet = new Set(expandedRequests.value)
			newSet.add(newId)
			expandedRequests.value = newSet
		}
	})
</script>

<style scoped>
	.line-clamp-2 {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	/* Highlight animation for focused request */
	@keyframes highlight-pulse {
		0%, 100% {
			box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
		}
		50% {
			box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.2);
		}
	}

	.focused-request {
		animation: highlight-pulse 1.5s ease-in-out 2;
	}
</style>
